(() => {
  const root = document.querySelector('[data-international-concierge]');
  if (!root) return;

  const form = root.querySelector('[data-concierge-form]');
  const status = root.querySelector('[data-concierge-status]');
  const mapPanel = root.querySelector('[data-concierge-map-panel]');
  const mapCanvas = root.querySelector('[data-concierge-map-canvas]');
  const mapLoading = root.querySelector('[data-concierge-map-loading]');
  const mapStatus = root.querySelector('[data-concierge-map-status]');
  const mapSearch = root.querySelector('[data-concierge-map-search]');
  const mapSuggestions = root.querySelector('[data-concierge-map-suggestions]');
  const mapOpen = root.querySelector('[data-concierge-map-open]');
  const mapSave = root.querySelector('[data-concierge-map-save]');
  const mapCancel = root.querySelector('[data-concierge-map-cancel]');
  const mapClear = root.querySelector('[data-concierge-map-clear]');
  const selectedPanel = root.querySelector('[data-concierge-selected-location]');
  const selectedAddress = root.querySelector('[data-concierge-selected-address]');
  const locationDetails = root.querySelector('[data-concierge-location-details]');
  const mapsReference = root.querySelector('[data-concierge-maps-reference]');
  const countryInput = form?.elements.namedItem('country');
  const cityInput = form?.elements.namedItem('city');

  const track = eventName => fetch('/apps/store-automation/api/revenue-events', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ event_name: eventName }),
  }).catch(() => {});

  root.querySelector('[data-concierge-start]')?.addEventListener('click', () => {
    track('international_concierge_started');
  });

  let mapsSdkPromise;
  let map;
  let marker;
  let geocoder;
  let autocompleteToken;
  let searchTimer;
  let pendingLocation;

  const loadMapsSdk = async () => {
    const apiKey = root.dataset.googleMapsApiKey;
    const mapId = root.dataset.googleMapsMapId;
    if (!apiKey || !mapId) throw new Error('Location search is temporarily unavailable. Country and city are still enough to submit your request.');
    if (window.google?.maps?.importLibrary) return { mapId };
    if (!mapsSdkPromise) {
      mapsSdkPromise = new Promise((resolve, reject) => {
        const callbackName = `conciergeMapsReady_${Math.random().toString(36).slice(2)}`;
        const script = document.createElement('script');
        const params = new URLSearchParams({
          key: apiKey,
          v: 'weekly',
          loading: 'async',
          libraries: 'maps,marker,places,geocoding',
          callback: callbackName,
        });
        window[callbackName] = () => {
          delete window[callbackName];
          resolve();
        };
        script.src = `https://maps.googleapis.com/maps/api/js?${params}`;
        script.async = true;
        script.onerror = () => {
          delete window[callbackName];
          reject(new Error('Location search is temporarily unavailable. Country and city are still enough to submit your request.'));
        };
        document.head.append(script);
      });
    }
    await mapsSdkPromise;
    return { mapId };
  };

  const clearSuggestions = () => {
    mapSuggestions?.replaceChildren();
    if (mapSuggestions) mapSuggestions.hidden = true;
    mapSearch?.setAttribute('aria-expanded', 'false');
  };

  const literalPosition = position => ({
    lat: typeof position?.lat === 'function' ? position.lat() : Number(position?.lat),
    lng: typeof position?.lng === 'function' ? position.lng() : Number(position?.lng),
  });

  const plusCodePrefix = /^[23456789CFGHJMPQRVWX]{4,8}\+[23456789CFGHJMPQRVWX]{2,3}\b[\s,]*/i;
  const withoutLeadingPlusCode = address => String(address || '').replace(plusCodePrefix, '').trim();
  const isMeaningfulAddress = address => {
    const readable = withoutLeadingPlusCode(address);
    return readable.length >= 5 && /[\p{L}]{3}/u.test(readable);
  };
  const resultScore = result => {
    const types = result?.types || [];
    let score = isMeaningfulAddress(result?.formatted_address) ? 20 : 0;
    if (types.includes('street_address')) score += 60;
    if (types.includes('premise') || types.includes('lodging')) score += 58;
    if (types.includes('route')) score += 45;
    if (types.some(type => type.startsWith('sublocality')) || types.includes('neighborhood')) score += 32;
    if (types.includes('locality')) score += 26;
    if (types.includes('plus_code')) score -= 70;
    return score;
  };
  const localityFallback = results => {
    const priorities = [
      'premise', 'lodging', 'neighborhood', 'sublocality_level_1', 'sublocality',
      'locality', 'administrative_area_level_2', 'administrative_area_level_1', 'country',
    ];
    const components = results.flatMap(result => result.address_components || []);
    return priorities.reduce((parts, type) => {
      const value = components.find(component => component.types?.includes(type))?.long_name?.trim();
      if (value && !parts.includes(value)) parts.push(value);
      return parts;
    }, []).join(', ');
  };
  const bestReverseGeocodedAddress = response => {
    const results = response?.results || [];
    const best = results
      .filter(result => isMeaningfulAddress(result.formatted_address))
      .sort((a, b) => resultScore(b) - resultScore(a))[0];
    if (best) return withoutLeadingPlusCode(best.formatted_address);
    const locality = localityFallback(results);
    if (isMeaningfulAddress(locality)) return locality;
    return [cityInput?.value, countryInput?.value].filter(Boolean).join(', ');
  };

  const componentValue = (components, type) => components
    .find(component => component.types?.includes(type))?.longText?.trim() || '';
  const withCountry = (address, components = []) => {
    const country = componentValue(components, 'country');
    if (!country || !address || address.toLocaleLowerCase().includes(country.toLocaleLowerCase())) return address;
    return `${address}, ${country}`;
  };
  const fillDestination = components => {
    const country = componentValue(components, 'country');
    const city = componentValue(components, 'locality') ||
      componentValue(components, 'administrative_area_level_2') ||
      componentValue(components, 'administrative_area_level_1');
    if (country && countryInput && !countryInput.value.trim()) countryInput.value = country;
    if (city && cityInput && !cityInput.value.trim()) cityInput.value = city;
  };

  const setPendingLocation = ({ position, formattedAddress, placeId = '', components = [] }) => {
    const literal = literalPosition(position);
    if (!Number.isFinite(literal.lat) || !Number.isFinite(literal.lng)) return;
    marker.position = literal;
    map.panTo(literal);
    if (map.getZoom() < 15) map.setZoom(16);
    pendingLocation = {
      latitude: literal.lat,
      longitude: literal.lng,
      formattedAddress: formattedAddress || '',
      placeId,
    };
    if (formattedAddress) mapSearch.value = formattedAddress;
    fillDestination(components);
    mapSave.disabled = !formattedAddress;
    mapStatus.textContent = formattedAddress ?
      'Location selected. Verify the pin, then use this location.' :
      'Pin selected. Search for the nearest recognizable place or address.';
  };

  const reverseGeocode = async position => {
    const literal = literalPosition(position);
    setPendingLocation({ position: literal, formattedAddress: '' });
    mapStatus.textContent = 'Finding the nearest recognizable address…';
    try {
      const response = await geocoder.geocode({ location: literal });
      const address = bestReverseGeocodedAddress(response);
      setPendingLocation({ position: literal, formattedAddress: address });
    } catch {
      mapStatus.textContent = 'Pin selected. Search for the hotel, residence, or nearby place to add a readable location.';
    }
  };

  const initializeMap = async () => {
    if (map) return;
    mapLoading.hidden = false;
    mapStatus.textContent = 'Loading secure map…';
    try {
      const config = await loadMapsSdk();
      const [{ Map }, { AdvancedMarkerElement, PinElement }, { Geocoder }] = await Promise.all([
        google.maps.importLibrary('maps'),
        google.maps.importLibrary('marker'),
        google.maps.importLibrary('geocoding'),
      ]);
      const defaultPosition = { lat: 25.2048, lng: 55.2708 };
      map = new Map(mapCanvas, {
        center: defaultPosition,
        zoom: 4,
        mapId: config.mapId,
        streetViewControl: false,
        fullscreenControl: false,
        mapTypeControl: false,
        clickableIcons: false,
        gestureHandling: 'greedy',
      });
      const pin = new PinElement({
        background: '#c8a96a',
        borderColor: '#12324a',
        glyphColor: '#12324a',
        scale: 1.1,
      });
      marker = new AdvancedMarkerElement({
        map,
        position: defaultPosition,
        content: pin,
        title: 'Selected private-care location',
        gmpDraggable: true,
      });
      geocoder = new Geocoder();
      map.addListener('click', event => reverseGeocode(event.latLng));
      marker.addListener('dragend', () => reverseGeocode(marker.position));
      mapLoading.hidden = true;
      mapStatus.textContent = 'Search for a place or position the pin. Exact location is optional.';
    } catch (error) {
      mapLoading.textContent = 'Map unavailable.';
      mapStatus.textContent = error.message;
    }
  };

  const openMap = async () => {
    mapPanel.hidden = false;
    await initializeMap();
    if (map) {
      setTimeout(() => {
        google.maps.event.trigger(map, 'resize');
        if (pendingLocation) map.panTo({ lat: pendingLocation.latitude, lng: pendingLocation.longitude });
      }, 50);
    }
    mapPanel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };

  const selectPrediction = async prediction => {
    clearSuggestions();
    await openMap();
    if (!map) return;
    mapStatus.textContent = 'Locating that place…';
    try {
      const place = prediction.toPlace();
      await place.fetchFields({ fields: ['id', 'displayName', 'formattedAddress', 'addressComponents', 'location'] });
      if (!place.location) throw new Error('Choose another location result.');
      const candidate = withoutLeadingPlusCode(place.formattedAddress || place.displayName || '');
      const fallback = [place.displayName, cityInput?.value, countryInput?.value].filter(Boolean).join(', ');
      const address = withCountry(isMeaningfulAddress(candidate) ? candidate : fallback, place.addressComponents);
      setPendingLocation({
        position: place.location,
        formattedAddress: address,
        placeId: place.id || '',
        components: place.addressComponents,
      });
      autocompleteToken = null;
    } catch (error) {
      mapStatus.textContent = error.message || 'Choose another location result.';
    }
  };

  const searchPlaces = async () => {
    const query = mapSearch.value.trim();
    if (query.length < 2) {
      clearSuggestions();
      return;
    }
    try {
      await loadMapsSdk();
      const { AutocompleteSessionToken, AutocompleteSuggestion } = await google.maps.importLibrary('places');
      autocompleteToken ||= new AutocompleteSessionToken();
      const { suggestions = [] } = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input: query,
        sessionToken: autocompleteToken,
        language: 'en',
      });
      const options = suggestions.slice(0, 6).map(({ placePrediction }) => {
        const option = document.createElement('button');
        option.type = 'button';
        option.className = 'ipc__map-suggestion';
        option.setAttribute('role', 'option');
        option.textContent = placePrediction.text?.toString() || placePrediction.mainText?.toString() || 'Location result';
        option.addEventListener('click', () => selectPrediction(placePrediction));
        return option;
      });
      mapSuggestions.replaceChildren(...options);
      mapSuggestions.hidden = options.length === 0;
      mapSearch.setAttribute('aria-expanded', String(options.length > 0));
    } catch (error) {
      clearSuggestions();
      mapSearch.setCustomValidity('');
      if (!mapPanel.hidden) mapStatus.textContent = error.message;
    }
  };

  const useSelectedLocation = () => {
    if (!pendingLocation?.formattedAddress) return;
    const query = `${pendingLocation.latitude},${pendingLocation.longitude}`;
    locationDetails.value = pendingLocation.formattedAddress;
    mapsReference.value = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
    selectedAddress.textContent = pendingLocation.formattedAddress;
    selectedPanel.hidden = false;
    mapPanel.hidden = true;
    mapStatus.textContent = 'Location added to this confidential request.';
  };

  const clearSelectedLocation = () => {
    pendingLocation = null;
    locationDetails.value = '';
    mapsReference.value = '';
    mapSearch.value = '';
    selectedAddress.textContent = '';
    selectedPanel.hidden = true;
    mapSave.disabled = true;
    clearSuggestions();
  };

  mapOpen?.addEventListener('click', openMap);
  mapCancel?.addEventListener('click', () => { mapPanel.hidden = true; });
  mapSave?.addEventListener('click', useSelectedLocation);
  mapClear?.addEventListener('click', clearSelectedLocation);
  mapSearch?.addEventListener('input', () => {
    clearTimeout(searchTimer);
    searchTimer = setTimeout(searchPlaces, 320);
  });
  mapSearch?.addEventListener('keydown', event => {
    if (event.key === 'Escape') clearSuggestions();
  });
  document.addEventListener('click', event => {
    if (!event.target.closest('[data-concierge-location-picker]')) clearSuggestions();
  });

  form?.addEventListener('submit', async event => {
    event.preventDefault();
    status.dataset.error = 'false';
    delete status.dataset.complete;
    status.textContent = 'Submitting your confidential request…';
    const submit = form.querySelector('button[type="submit"]');
    submit.disabled = true;
    if (!locationDetails.value && mapSearch.value.trim()) locationDetails.value = mapSearch.value.trim();
    const payload = Object.fromEntries(new FormData(form).entries());
    try {
      const response = await fetch('/apps/store-automation/api/international-concierge', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Unable to submit request');
      const confirmationTitle = document.createElement('strong');
      confirmationTitle.textContent = 'Your confidential request has been received';
      const confirmationIntro = document.createElement('span');
      confirmationIntro.textContent = 'Your request will be reviewed privately for availability, travel requirements and the proposed scope of care.';
      const confirmationDetails = [
        ['Private reference', data.reference],
        ['Destination', [payload.city, payload.country].filter(Boolean).join(', ')],
        ['Preferred dates', payload.preferred_dates || 'To be confirmed'],
        ['Requested care', payload.service_type],
      ];
      status.replaceChildren(confirmationTitle, confirmationIntro, ...confirmationDetails.map(([label, value]) => {
        const line = document.createElement('span');
        line.textContent = `${label}: ${value}`;
        return line;
      }));
      status.dataset.complete = 'true';
      form.reset();
      clearSelectedLocation();
      mapPanel.hidden = true;
      track('international_concierge_submitted');
    } catch {
      status.dataset.error = 'true';
      delete status.dataset.complete;
      status.textContent = 'We could not submit your request. Please retry or use the clinic contact page.';
    } finally {
      submit.disabled = false;
    }
  });
})();
